import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, History, Lock, Trophy, PartyPopper, Shell, Maximize2, X } from "lucide-react";
import { api, USER_ID, track } from "../api";
import { MelaTopBar } from "../arcade/MelaTopBar";
import { SpinChakra } from "../arcade/SpinChakra";
import { FlavorTrivia } from "../arcade/FlavorTrivia";
import { GoldenScratch } from "../arcade/GoldenScratch";
import { StreetFoodRun } from "../arcade/StreetFoodRun";
import { MasalaMatch } from "../arcade/MasalaMatch";
import { RingToss } from "../arcade/RingToss";
import { SaapSeedi } from "../arcade/SaapSeedi";
import { PunjabiLudo } from "../arcade/PunjabiLudo";
import { CarromChaupal } from "../arcade/CarromChaupal";
import { KancheChampion } from "../arcade/KancheChampion";
import { GameScaler } from "../arcade/GameScaler";
import { CricketClash } from "../arcade/CricketClash";
import { BadgesSheet } from "../arcade/BadgesSheet";
import { PhoneGate, openPhoneGate } from "../arcade/PhoneGate";
import { useSeo } from "../seo";
import { toast } from "sonner";

const AREAS = [
  {
    key: "featured", title: "Featured Match", pa: "ਖਾਸ ਮੁਕਾਬਲਾ", desc: "Two icons. One pitch. One final over.",
    games: [
      { key: "cricket", name: "Urban vs Regal — Cricket Clash", pa: "ਕ੍ਰਿਕਟ ਕਲੈਸ਼", desc: "Six balls to chase the target — Urban Mascot vs Mr. Regal on the mela pitch", accent: "#F0C742", featured: true },
    ],
  },
  {
    key: "classics", title: "Mela Classics", pa: "ਮੇਲਾ ਕਲਾਸਿਕ", desc: "The stalls everyone runs to first.",
    games: [
      { key: "chakkar", name: "Mela Da Chakkar", pa: "ਮੇਲੇ ਦਾ ਚੱਕਰ", desc: "The prize wheel — one free spin a day", accent: "#D4AF37" },
      { key: "ringtoss", name: "Choori Da Challa", pa: "ਚੂੜੀ ਦਾ ਛੱਲਾ", desc: "Ring toss onto clay-pot pegs", accent: "#E25822" },
    ],
  },
  {
    key: "playground", title: "Punjab Playground", pa: "ਪੰਜਾਬ ਖੇਡ ਮੈਦਾਨ", desc: "Fast games, big bragging rights.",
    games: [
      { key: "runner", name: "Pind Food Run", pa: "ਪਿੰਡ ਫੂਡ ਰਨ", desc: "Jump the carts — weekly leaderboard", accent: "#E25822" },
      { key: "kanche", name: "Kanche Champion", pa: "ਕੰਚੇ", desc: "Flick the shooter — knock marbles from the circle", accent: "#4FA3D1" },
    ],
  },
  {
    key: "chaupal", title: "Family Chaupal", pa: "ਪਰਿਵਾਰਕ ਚੌਪਾਲ", desc: "Games to play together at the table.",
    games: [
      { key: "saap", name: "Saap-Seedi", pa: "ਸੱਪ-ਸੀੜੀ", desc: "Snakes & ladders — 2-4 players, one table", accent: "#2E9E5B" },
      { key: "ludo", name: "Kaudi Chaupar", pa: "ਕੌਡੀ ਚੌਪੜ", desc: "Throw kaudi shells, race beads to the ghar — 2-4 players", accent: "#F97316" },
      { key: "carrom", name: "Carrom Chaupal", pa: "ਕੈਰਮ", desc: "Real striker physics — rani and all, 2 players", accent: "#D9A85C" },
    ],
  },
  {
    key: "flavor", title: "Food & Flavor Games", pa: "ਸੁਆਦ ਦੀਆਂ ਖੇਡਾਂ", desc: "Play with the food you love.",
    games: [
      { key: "trivia", name: "Punjab Flavor Pehchaan", pa: "ਸੁਆਦ ਪਛਾਣ", desc: "Daily question + weekly Ustaad round", accent: "#E25822" },
      { key: "masala", name: "Masala Match", pa: "ਮਸਾਲਾ ਮਿਲਾਨ", desc: "Match spices to their famous dishes", accent: "#D4AF37" },
    ],
  },
  {
    key: "rewards", title: "Golden Rewards Tent", pa: "ਸੁਨਹਿਰੀ ਇਨਾਮ ਤੰਬੂ", desc: "Where real prizes live.",
    games: [
      { key: "scratch", name: "Sunehri Patti", pa: "ਸੁਨਹਿਰੀ ਪੱਟੀ", desc: "Scratch cards after every completed order", accent: "#D4AF37" },
    ],
  },
];

const reasonLabel = (r) => {
  if (r.startsWith("spin:")) return "Mela Da Chakkar";
  if (r.startsWith("scratch:")) return "Sunehri Patti";
  if (r.startsWith("trivia-streak")) return "Pehchaan 7-day streak";
  if (r.startsWith("trivia")) return "Flavor Pehchaan";
  if (r.startsWith("ustaad")) return "Flavor Ustaad round";
  if (r.startsWith("runner-run")) return "Pind Food Run";
  if (r.startsWith("runner:")) return "Food Run weekly prize";
  if (r.startsWith("masala")) return "Masala Match";
  if (r.startsWith("ringtoss")) return "Choori Da Challa";
  if (r.startsWith("saap")) return "Saap-Seedi family win";
  if (r.startsWith("ludo")) return "Kaudi Chaupar win";
  if (r.startsWith("carrom")) return "Carrom Chaupal win";
  if (r.startsWith("kanche")) return "Kanche Champion";
  if (r.startsWith("latoo")) return "Lattoo Master spin";
  return r;
};

const EventCountdown = ({ end }) => {
  const [leftStr, setLeftStr] = useState("");
  useEffect(() => {
    const target = new Date(`${end}T23:59:59`);
    const tick = () => {
      const ms = target - Date.now();
      if (ms <= 0) return setLeftStr("");
      const d = Math.floor(ms / 86400000), h = Math.floor(ms / 3600000) % 24,
        m = Math.floor(ms / 60000) % 60, s = Math.floor(ms / 1000) % 60;
      setLeftStr(`${d > 0 ? `${d}D ` : ""}${String(h).padStart(2, "0")}H ${String(m).padStart(2, "0")}M ${String(s).padStart(2, "0")}S`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [end]);
  if (!leftStr) return null;
  return (
    <span data-testid="mela-event-countdown" className="text-[10px] font-black text-[#E25822] tabular-nums whitespace-nowrap">
      ENDS IN {leftStr}
    </span>
  );
};

const WinnerWall = ({ myNickname, onNick }) => {
  const [wall, setWall] = useState(null);
  const [nick, setNick] = useState("");
  const [savingNick, setSavingNick] = useState(false);
  useEffect(() => { api.get("/arcade/winner-wall").then((r) => setWall(r.data)).catch(() => {}); }, []);
  if (!wall || (!wall.champions.length && !wall.current && !wall.kaudi?.length && !wall.coins?.length && !wall.ustaad?.length)) return null;
  const saveNick = async () => {
    setSavingNick(true);
    try {
      await api.post("/arcade/nickname", { user_id: USER_ID, nickname: nick });
      toast.success("Nickname saved — see you on the wall!", { style: { background: "#1A1A1A", color: "#D4AF37" } });
      const r = await api.get("/arcade/winner-wall");
      setWall(r.data);
      onNick?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save nickname"); }
    setSavingNick(false);
  };
  return (
    <section data-testid="winner-wall" className="mt-7 rounded-2xl border border-[#8a6d1f]/30 bg-[#14100A] p-4">
      <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-bold flex items-center gap-1.5"><Trophy size={12} /> WINNER WALL · ਜੇਤੂ ਕੰਧ</p>
      <p className="text-[11px] text-white/45">Pind Food Run weekly champions — carved into the mela wall</p>
      {wall.current && (
        <div data-testid="winner-wall-current" className="mt-2.5 rounded-xl border border-[#E25822]/40 bg-[#1C1006] px-3.5 py-2.5 flex items-center justify-between text-xs">
          <span className="font-black text-[#E25822]">THIS WEEK'S LEADER</span>
          <span className="font-black">{wall.current.nickname} · {wall.current.score}</span>
        </div>
      )}
      {wall.champions.length > 0 && (
        <div className="mt-2 divide-y divide-white/5">
          {wall.champions.map((c) => (
            <div key={c.week} data-testid={`winner-wall-row-${c.week}`} className="flex items-center justify-between py-2 text-xs">
              <span className="text-white/45 font-bold">{c.week}</span>
              <span className="font-black text-[#D4AF37] flex items-center gap-1.5"><Trophy size={11} /> {c.nickname}</span>
              <span className="text-white/70 font-bold">{c.score}</span>
            </div>
          ))}
        </div>
      )}
      {wall.kaudi?.length > 0 && (
        <div data-testid="winner-wall-kaudi" className="mt-3 rounded-xl border border-[#E3B23C]/25 bg-[#171208] p-3">
          <p className="text-[9px] tracking-[0.25em] text-[#E3B23C] font-black flex items-center gap-1.5"><Shell size={11} /> ਕੌਡੀ ਚੌਪੜ · BEST-OF-3 CHAMPIONS THIS WEEK</p>
          <div className="mt-1 divide-y divide-white/5">
            {wall.kaudi.map((m, i) => (
              <div key={i} data-testid={`kaudi-champ-row-${i}`} className="flex items-center justify-between py-1.5 text-xs">
                <span className="font-black text-[#F0C742]">{m.winner}{m.nickname ? ` · ${m.nickname}` : ""}</span>
                <span className="text-white/60 font-bold tabular-nums">{m.score}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {wall.ustaad?.length > 0 && (
        <div data-testid="winner-wall-ustaad" className="mt-3 rounded-xl border border-[#F0C742]/30 bg-[#171208] p-3">
          <p className="text-[9px] tracking-[0.25em] text-[#F0C742] font-black flex items-center gap-1.5">
            <Trophy size={11} /> ਕ੍ਰਿਕਟ ਉਸਤਾਦ · CRICKET USTAAD CONQUERORS
          </p>
          <p className="text-[9px] text-white/35 mt-0.5">Carved forever — the few who beat the final test</p>
          <div className="mt-1 divide-y divide-white/5">
            {wall.ustaad.map((m, i) => (
              <div key={i} data-testid={`ustaad-champ-row-${i}`} className="flex items-center justify-between gap-2 py-1.5 text-xs">
                <span className="font-black text-[#F0C742] min-w-0 truncate">{m.nickname}</span>
                <span className="text-white/45 font-bold text-[9px] shrink-0 uppercase">
                  TEAM {m.team}{m.golden ? " · GOLDEN BALL" : ""}
                </span>
                <span className="font-black tabular-nums shrink-0">{m.score}/{m.target}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {wall.coins?.length > 0 && (
        <div data-testid="winner-wall-coins" className="mt-3 rounded-xl border border-[#F0C742]/25 bg-[#171208] p-3">
          <p className="text-[9px] tracking-[0.25em] text-[#F0C742] font-black flex items-center gap-1.5">
            <img src="/mela/coin.png" alt="Regal Coins" className="w-3.5 h-3.5 object-contain" /> REGAL COINS LEADERBOARD · FUN POINTS ONLY
          </p>
          <div className="mt-1 divide-y divide-white/5">
            {wall.coins.map((c, i) => (
              <div key={i} data-testid={`coin-leader-row-${i}`} className="flex items-center justify-between gap-2 py-1.5 text-xs">
                <span className="font-black min-w-0 truncate">{i + 1}. {c.nickname}</span>
                <span className="text-white/45 font-bold text-[10px] shrink-0">{c.level}</span>
                <span className="font-black text-[#F0C742] tabular-nums shrink-0">{c.lifetime}</span>
              </div>
            ))}
          </div>
          {!myNickname && (
            <div data-testid="coin-nickname-form" className="mt-2.5 border-t border-white/5 pt-2.5">
              <p className="text-[9px] text-white/40 font-bold">You're not on the wall yet — pick a nickname to appear with your coins:</p>
              <div className="flex items-center gap-2 mt-1.5">
                <input data-testid="coin-nickname-input" value={nick} maxLength={14} placeholder="Your mela name"
                  onChange={(e) => setNick(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveNick()}
                  className="flex-1 min-w-0 bg-[#121212] border border-[#D4AF37]/30 rounded-full px-3.5 h-9 text-xs focus:outline-none focus:border-[#D4AF37]/70" />
                <button data-testid="coin-nickname-save" onClick={saveNick} disabled={savingNick || nick.trim().length < 3}
                  className="h-9 px-4 rounded-full bg-[#D4AF37] text-black text-[9px] font-black tracking-widest disabled:opacity-50 shrink-0">
                  {savingNick ? "…" : "SAVE"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const Bunting = () => (
  <div className="mela-bunting flex overflow-hidden h-5 -mt-px" aria-hidden="true">
    {Array.from({ length: 40 }).map((_, i) => (
      <span key={i} className="shrink-0 w-0 h-0 mx-0.5"
        style={{ borderLeft: "9px solid transparent", borderRight: "9px solid transparent",
          borderTop: `16px solid ${["#E25822", "#D4AF37", "#1D5C3A", "#274690", "#B33771"][i % 5]}` }} />
    ))}
  </div>
);

export default function ArcadePage() {
  useSeo({ title: "Urban Arcade", noindex: true, path: "/experience/arcade" });
  const [state, setState] = useState(null);
  const [game, setGame] = useState(null);
  const [showBadges, setShowBadges] = useState(false);
  const [full, setFull] = useState(false);
  const rewardsRef = useRef(null);
  useEffect(() => {
    document.body.style.overflow = full ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [full]);
  const refresh = useCallback(
    () => api.get("/arcade/state", { params: { user_id: USER_ID } }).then((r) => setState(r.data)).catch(() => {}),
    []
  );
  useEffect(() => { refresh(); track("arcade_opened", {}); }, [refresh]);

  if (!state) {
    return (
      <div className="px-4 pt-10 flex justify-center" data-testid="arcade-loading">
        <Loader2 size={22} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const enabled = state.games_enabled || {};
  const unscratched = (state.scratch_cards || []).filter((c) => c.status === "hidden").length;

  const GameView = {
    chakkar: <SpinChakra state={state} refresh={refresh} />,
    trivia: <FlavorTrivia state={state} refresh={refresh} />,
    scratch: <GoldenScratch state={state} refresh={refresh} />,
    runner: <StreetFoodRun state={state} refresh={refresh} />,
    masala: <MasalaMatch refresh={refresh} />,
    ringtoss: <RingToss refresh={refresh} />,
    saap: <SaapSeedi refresh={refresh} />,
    ludo: <PunjabiLudo refresh={refresh} />,
    carrom: <CarromChaupal refresh={refresh} />,
    kanche: <KancheChampion refresh={refresh} />,
    cricket: <CricketClash refresh={refresh} ustaadUnlocked={!!state.cricket_ustaad_unlocked} />,
  }[game];

  return (
    <div data-testid="arcade-page" className="pb-12">
      {/* MELA GATEWAY */}
      <div className="relative -mt-0">
        <div className="relative h-56 sm:h-72 lg:h-80 overflow-hidden">
          <img src="/mela/gateway.jpg" alt="Purana Punjab Mela gateway" className="absolute inset-0 w-full h-full object-cover mela-hero-img" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 px-4 pb-4 text-center">
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight drop-shadow-lg">Urban Arcade</h1>
            <p data-testid="mela-title" className="text-sm sm:text-base font-black tracking-[0.3em] text-[#F0C742] mt-1 drop-shadow">ਪੁਰਾਣਾ ਪੰਜਾਬ ਮੇਲਾ · PURANA PUNJAB MELA</p>
            <p className="font-display italic text-white/85 text-sm sm:text-base mt-1 drop-shadow">"Step back into the Punjab we remember."</p>
          </div>
        </div>
        <Bunting />
      </div>

      <div className="px-4 mx-auto w-full max-w-4xl xl:max-w-none xl:px-8 2xl:px-12">
        <MelaTopBar state={state} onBadges={() => setShowBadges(true)}
          onRewards={() => { setGame("scratch"); setTimeout(() => rewardsRef.current?.scrollIntoView({ behavior: "smooth" }), 150); }} />

        {state.phone_linked === false && (
          <div data-testid="trial-banner" className="mt-3 rounded-2xl border border-[#F0C742]/40 bg-[#1C160A] px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[11px] font-black text-[#F0C742]">
              TRIAL MODE · {state.trial_left} free play{state.trial_left === 1 ? "" : "s"} left today
            </p>
            <button data-testid="trial-add-phone" onClick={openPhoneGate}
              className="text-[9px] font-black tracking-widest text-black bg-[#F0C742] rounded-full px-3.5 py-1.5">
              ADD PHONE — SAVE YOUR COINS
            </button>
          </div>
        )}

        {state.event && (
          <div data-testid="mela-event-banner" className="mt-3 rounded-2xl border border-[#E25822]/50 bg-gradient-to-r from-[#2A1206] to-[#1C160A] px-4 py-3 flex items-center gap-3">
            <PartyPopper size={20} className="text-[#F0C742] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-black text-[#F0C742] flex items-center gap-1.5 flex-wrap"><img src="/mela/coin.png" alt="Regal Coins" className="w-4 h-4 object-contain shrink-0" /> {state.event.name}{state.event.multiplier > 1 ? ` — ${state.event.multiplier}x REGAL COINS ON EVERY GAME` : ""}</p>
              <p className="text-[10px] text-white/55">{state.event.message || "Special mela night — play while it lasts!"}</p>
            </div>
            <EventCountdown end={state.event.end} />
          </div>
        )}

        {game ? (
          <div data-testid={full ? "game-fullscreen-overlay" : "game-inline-view"}
            className={full ? "fixed inset-0 z-[999] overflow-y-auto bg-[#0D0A05] px-3 sm:px-4 pt-3 pb-10" : "mt-4"}>
            <div className={full ? "mx-auto w-full max-w-3xl xl:max-w-none xl:px-4" : ""}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <button data-testid="back-to-mela" onClick={() => { setGame(null); setFull(false); refresh(); }}
                  className="text-[10px] xl:text-sm font-black tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-4 py-2 xl:px-6 xl:py-3">
                  ← BACK TO THE MELA
                </button>
                <button data-testid={full ? "game-fullscreen-exit" : "game-fullscreen-btn"} onClick={() => setFull((f) => !f)}
                  aria-label={full ? "Exit full screen" : "Play full screen"}
                  className={`inline-flex items-center gap-1.5 text-[10px] xl:text-sm font-black tracking-widest rounded-full px-4 py-2 xl:px-6 xl:py-3 border transition-colors ${full ? "text-black bg-[#F0C742] border-[#F0C742]" : "text-[#F0C742] border-[#F0C742]/50 hover:bg-[#F0C742]/10"}`}>
                  {full ? <><X size={12} /> EXIT</> : <><Maximize2 size={12} /> FULL SCREEN</>}
                </button>
              </div>
              <GameScaler>{GameView}</GameScaler>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-7">
            {AREAS.map((area) => {
              const visible = area.games.filter((g) => g.soon || enabled[g.key] !== false);
              if (!visible.length) return null;
              return (
                <section key={area.key} data-testid={`mela-area-${area.key}`}
                  className="relative rounded-2xl border border-[#8a6d1f]/30 overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.16] pointer-events-none"
                    style={{ backgroundImage: "url(/mela/stalls.jpg)", backgroundSize: "cover", backgroundPosition: "center" }} />
                  <div className="relative p-4">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h2 className="font-display text-lg xl:text-2xl font-bold">{area.title}</h2>
                      <span className="text-xs xl:text-base text-[#F0C742]/80 font-bold">{area.pa}</span>
                    </div>
                    <p className="text-[11px] xl:text-sm text-white/45">{area.desc}</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2.5 xl:gap-4 mt-3">
                      {visible.map((g) =>
                        g.soon ? (
                          <div key={g.key} data-testid={`mela-stall-${g.key}`}
                            className="mela-stall rounded-xl border border-white/10 bg-[#14100A]/90 p-3.5 opacity-70">
                            <div className="mela-canopy mb-2.5" />
                            <p className="text-xs font-black flex items-center gap-1.5">{g.name} <Lock size={10} className="text-white/30" /></p>
                            <p className="text-[10px] text-[#F0C742]/60 font-bold">{g.pa}</p>
                            <p className="text-[10px] text-white/40 mt-1">{g.desc}</p>
                            <span className="inline-block mt-2 text-[8px] font-black tracking-widest text-white/40 border border-white/15 rounded-full px-2.5 py-1">OPENING SOON</span>
                          </div>
                        ) : (
                          <button key={g.key} data-testid={`mela-stall-${g.key}`} onClick={() => { setGame(g.key); track("arcade_game_opened", { game: g.key }); }}
                            className="mela-stall text-left rounded-xl border border-[#8a6d1f]/40 bg-[#14100A]/90 p-3.5 xl:p-5 hover:border-[#D4AF37]/70 transition-colors">
                            <div className="mela-canopy mb-2.5" />
                            <p className="text-xs xl:text-lg font-black" style={{ color: g.accent }}>{g.name.toUpperCase()}{g.featured && <span className="ml-1.5 text-[7px] xl:text-[10px] font-black tracking-widest text-black bg-[#F0C742] rounded-full px-1.5 py-0.5 align-middle">FEATURED</span>}</p>
                            <p className="text-[10px] xl:text-sm text-[#F0C742]/70 font-bold">{g.pa}</p>
                            <p className="text-[10px] xl:text-sm text-white/50 mt-1">{g.desc}</p>
                            <span className="inline-flex items-center gap-1 mt-2 xl:mt-3 text-[8px] xl:text-xs font-black tracking-widest text-black bg-[#D4AF37] rounded-full px-3 py-1.5 xl:px-5 xl:py-2.5">
                              PLAY {g.key === "scratch" && unscratched > 0 ? `· ${unscratched} CARD${unscratched > 1 ? "S" : ""}` : ""}
                            </span>
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {!game && <WinnerWall myNickname={state.nickname} onNick={refresh} />}

        {/* REWARDS HISTORY */}
        {(state.history.length > 0 || state.codes.length > 0) && (
          <div ref={rewardsRef} className="mt-7" data-testid="arcade-history">
            <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-bold flex items-center gap-1.5"><History size={12} /> MELA REWARDS LEDGER</p>
            {state.codes.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {state.codes.map((c) => (
                  <div key={c.code} data-testid={`arcade-code-${c.code}`}
                    className={`flex items-center justify-between rounded-xl border px-3.5 py-2.5 text-xs ${c.expired || c.status !== "issued" ? "border-white/5 text-white/30" : "border-[#D4AF37]/30 bg-[#1C160A]"}`}>
                    <span className="min-w-0 truncate pr-2">{c.name}</span>
                    <span className="font-mono font-black tracking-widest shrink-0">
                      {c.code}{c.expired ? " · EXPIRED" : c.status !== "issued" ? " · USED" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {state.history.length > 0 && (
              <div className="mt-2 divide-y divide-white/5">
                {state.history.map((h, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 text-xs">
                    <span className="text-white/60">{reasonLabel(h.reason)}</span>
                    <span className={`font-black flex items-center gap-1 ${h.points >= 0 ? (h.currency === "mela" ? "text-[#F0C742]" : "text-[#D4AF37]") : "text-[#E25822]"}`}>
                      {h.points >= 0 ? `+${h.points}` : h.points}
                      {h.currency === "mela"
                        ? <img src="/mela/coin.png" alt="Regal Coins" className="w-3.5 h-3.5 object-contain" />
                        : <span className="text-[8px] tracking-widest text-white/40">UP</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showBadges && <BadgesSheet badges={state.badges || []} onClose={() => setShowBadges(false)} />}
      <PhoneGate onLinked={refresh} />
    </div>
  );
}
